import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { offersApi, type Offer, type OfferNote, type OfferActivityEntry } from "../../api/offers";
import type { OfferStage } from "../../api/offerStages";
import { useToast } from "../Toast";
import type { ApiError } from "../../api/client";
import Ic from "../icons.jsx";
import { isOverdue } from "../../pages/offersBoard";
import {
  stepStates, wonStage, lostStage,
  sequentialStepColor, STATUS_GOOD, STATUS_CRITICAL,
  isoToLocalInputValue, localInputValueToIso,
  type PipelineStep,
} from "../../pages/offerPipeline";
import { intlLocale } from "../../lib/format";

/**
 * OfferEditorPage's pipeline header: a stepper over the OPEN stages + the two locked WON/LOST outcomes
 * (click = free-movement transition, same "kao CRM" rule the board's
 * drag&drop already follows — no FSM gating on which step is clickable),
 * a rok/reminder picker, a tag picker, and the Beleške/Aktivnost tabs.
 * Self-contained: owns its own notes/activity fetch and every mutation
 * (transition/due/tags), bubbling the updated `Offer` back to the parent via
 * `onOfferChange` so the items/PDF section below keeps working unmodified.
 *
 * Tag picker follows TmTagPicker's chip+input shape (chips with × to
 * detach, type-and-add to attach) but *not* its autocomplete/reuse-suggestion
 * dropdown: unlike tm_tag, there is no `GET` list endpoint for offer_tag (see
 * OfferController) — the only server contract is find-or-create via POST
 * .../tags — so there's nothing to suggest against; a list endpoint can be
 * added later rather than invented here.
 */
export function OfferPipelineHeader({
  offer, stages, onOfferChange, stageLocked = false,
}: {
  offer: Offer;
  stages: OfferStage[];
  onOfferChange: (o: Offer) => void;
  /** The stage is not the portal's to change (an offer from the ERP); notes, tags and reminders stay. */
  stageLocked?: boolean;
}) {
  const { canWrite } = useSectionAccess("offers");
  const { t } = useTranslation();
  const toast = useToast();

  const [busy, setBusy] = useState(false);
  const [dueValue, setDueValue] = useState(() => isoToLocalInputValue(offer.dueAt));
  const [tagDraft, setTagDraft] = useState("");
  const [tagBusy, setTagBusy] = useState(false);

  const [tab, setTab] = useState<"notes" | "activity">("notes");
  const [notes, setNotes] = useState<OfferNote[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [activity, setActivity] = useState<OfferActivityEntry[]>([]);

  useEffect(() => {
    setDueValue(isoToLocalInputValue(offer.dueAt));
  }, [offer.dueAt]);

  useEffect(() => {
    offersApi.listNotes(offer.id).then(setNotes).catch(() => setNotes([]));
    refreshActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id]);

  function refreshActivity() {
    offersApi.activity(offer.id).then(setActivity).catch(() => setActivity([]));
  }

  async function transitionTo(stage: OfferStage) {
    if (!canWrite || stageLocked) return;
    if (stage.id === offer.stage?.id) return;
    setBusy(true);
    try {
      const updated = await offersApi.transition(offer.id, stage.id);
      onOfferChange(updated);
      refreshActivity();
      toast.success(t("offers.toast.moved", { stage: stage.name }));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveDue(nextValue: string) {
    if (!canWrite) return;
    setBusy(true);
    try {
      const iso = localInputValueToIso(nextValue);
      const updated = await offersApi.setDue(offer.id, iso);
      onOfferChange(updated);
      refreshActivity();
      toast.success(t("offers.pipeline.due.saved", "Reminder saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function attachTag() {
    if (!canWrite) return;
    const name = tagDraft.trim();
    if (!name) return;
    setTagBusy(true);
    try {
      const updated = await offersApi.attachTag(offer.id, name);
      onOfferChange(updated);
      setTagDraft("");
      refreshActivity();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setTagBusy(false);
    }
  }

  async function detachTag(tagId: string) {
    if (!canWrite) return;
    setTagBusy(true);
    try {
      const updated = await offersApi.detachTag(offer.id, tagId);
      onOfferChange(updated);
      refreshActivity();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setTagBusy(false);
    }
  }

  async function addNote() {
    if (!canWrite) return;
    const body = noteDraft.trim();
    if (!body) return;
    setNoteBusy(true);
    try {
      const created = await offersApi.addNote(offer.id, body);
      setNotes((prev) => [...prev, created]);
      setNoteDraft("");
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setNoteBusy(false);
    }
  }

  const steps = stepStates(stages, offer.stage?.id ?? null);
  const won = wonStage(stages);
  const lost = lostStage(stages);
  const dueDirty = dueValue !== isoToLocalInputValue(offer.dueAt);
  const overdue = isOverdue(offer);

  return (
    <section style={card}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Stepper
          steps={steps}
          won={won}
          lost={lost}
          currentStageId={offer.stage?.id ?? null}
          busy={busy || !canWrite || stageLocked}
          onTransition={transitionTo}
        />

        <div style={fieldsRow}>
          <div style={fieldGroup}>
            <span style={fieldLabel}>{t("offers.pipeline.due.label", "Reminder")}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="datetime-local"
                value={dueValue}
                onChange={(e) => setDueValue(e.target.value)}
                disabled={busy || !canWrite}
                className="input"
                style={{ width: 210 }}
              />
              {dueDirty && (
                <button type="button" className="btn primary sm" disabled={busy || !canWrite} onClick={() => saveDue(dueValue)}>
                  <Ic.check />
                </button>
              )}
              {(offer.dueAt || dueValue) && (
                <button
                  type="button"
                  className="btn ghost sm"
                  disabled={busy || !canWrite}
                  onClick={() => saveDue("")}
                  aria-label={t("offers.pipeline.due.clear", "Clear reminder")}
                >
                  <Ic.x />
                </button>
              )}
              {overdue && <span style={overdueBadge}>{t("offers.pipeline.due.overdue", "Overdue")}</span>}
            </div>
          </div>

          <div style={{ ...fieldGroup, flex: 1, minWidth: 240 }}>
            <span style={fieldLabel}>{t("offers.pipeline.tags.label", "Tags")}</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              {offer.tags.map((tag) => (
                <span key={tag.id} style={tagChip(tag.color)}>
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => void detachTag(tag.id)}
                    disabled={!canWrite || tagBusy || !canWrite}
                    aria-label={t("offers.pipeline.tags.remove", { name: tag.name, defaultValue: "Remove {{name}}" })}
                    style={tagX}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void attachTag(); } }}
                placeholder={t("offers.pipeline.tags.add", "Add tag…")}
                disabled={!canWrite || tagBusy || !canWrite}
                className="input"
                style={{ height: 28, padding: "0 8px", fontSize: 12, width: 140 }}
              />
              <button type="button" className="btn ghost sm" disabled={!canWrite || tagBusy || !tagDraft.trim()} onClick={() => void attachTag()}>
                <Ic.plus />
              </button>
            </div>
          </div>
        </div>

        <div>
          <div role="tablist" aria-label={t("offers.pipeline.tabs.aria", "Notes / activity")} style={tabBar}>
            <button type="button" role="tab" aria-selected={tab === "notes"} onClick={() => setTab("notes")} style={tabBtnStyle(tab === "notes")}>
              {t("offers.pipeline.tabs.notes", "Notes")}
            </button>
            <button type="button" role="tab" aria-selected={tab === "activity"} onClick={() => setTab("activity")} style={tabBtnStyle(tab === "activity")}>
              {t("offers.pipeline.tabs.activity", "Activity")}
            </button>
          </div>

          {tab === "notes" ? (
            <NotesPanel notes={notes} draft={noteDraft} onDraftChange={setNoteDraft} busy={noteBusy || !canWrite} onSend={() => void addNote()} />
          ) : (
            <ActivityPanel entries={activity} />
          )}
        </div>
      </div>
    </section>
  );
}

function Stepper({
  steps, won, lost, currentStageId, busy, onTransition,
}: {
  steps: PipelineStep<OfferStage>[];
  won: OfferStage | null;
  lost: OfferStage | null;
  currentStageId: string | null;
  busy: boolean;
  onTransition: (stage: OfferStage) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={stepperRow}>
      {steps.map((step, i) => {
        const color = sequentialStepColor(i, steps.length);
        return (
          <div key={step.stage.id} style={stepWrap}>
            {i > 0 && <span aria-hidden style={{ ...connector, background: step.state === "upcoming" ? "var(--border)" : color }} />}
            <button
              type="button"
              disabled={busy || step.state === "current"}
              onClick={() => onTransition(step.stage)}
              style={stepBtn(step.state, color)}
              title={step.stage.name}
            >
              {step.state === "done" ? (
                <Ic.check aria-hidden style={{ width: 13, height: 13, color, flex: "none" }} />
              ) : (
                <span aria-hidden style={stepDot(step.state, color)} />
              )}
              {step.stage.name}
            </button>
          </div>
        );
      })}

      {(won || lost) && <span aria-hidden style={outcomeDivider} />}

      {won && (
        <button
          type="button"
          disabled={busy || won.id === currentStageId}
          onClick={() => onTransition(won)}
          style={outcomeBtn(won.id === currentStageId, STATUS_GOOD)}
        >
          <Ic.check /> {won.name}
        </button>
      )}
      {lost && (
        <button
          type="button"
          disabled={busy || lost.id === currentStageId}
          onClick={() => onTransition(lost)}
          style={outcomeBtn(lost.id === currentStageId, STATUS_CRITICAL)}
        >
          <Ic.x /> {lost.name}
        </button>
      )}
      {steps.length === 0 && !won && !lost && (
        <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>{t("offers.pipeline.noStages", "No pipeline stages configured.")}</span>
      )}
    </div>
  );
}

function NotesPanel({
  notes, draft, onDraftChange, busy, onSend,
}: {
  notes: OfferNote[];
  draft: string;
  onDraftChange: (v: string) => void;
  busy: boolean;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 4 }}>
      {notes.length === 0 ? (
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("offers.pipeline.notes.empty", "No notes yet.")}</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {notes.map((n) => (
            <li key={n.id} style={noteCard}>
              <div style={noteHead}>
                <span aria-hidden style={authorAvatar}>{initials(n.authorName)}</span>
                <b style={{ color: "var(--ink)" }}>{n.authorName ?? "—"}</b>
                <span>· {fmtDateTime(n.createdAt)}</span>
              </div>
              <div style={{ color: "var(--ink-2)", fontSize: 13.5, whiteSpace: "pre-wrap", marginTop: 4 }}>{n.body}</div>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <textarea
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          rows={2}
          maxLength={5000}
          placeholder={t("offers.pipeline.notes.placeholder", "Add a note…")}
          disabled={busy}
          className="input"
          style={{ resize: "vertical" }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="btn primary sm" onClick={onSend} disabled={busy || !draft.trim()}>
            {busy ? t("offers.pipeline.notes.sending", "Saving…") : t("offers.pipeline.notes.send", "Add note")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityPanel({ entries }: { entries: OfferActivityEntry[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) {
    return <div style={{ color: "var(--ink-3)", fontSize: 13, paddingTop: 4 }}>{t("offers.pipeline.activity.empty", "No activity yet.")}</div>;
  }
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "4px 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
      {entries.map((e, i) => (
        <li key={i} style={activityRow}>
          <span aria-hidden style={{ ...activityDot, background: e.type === "STAGE_CHANGE" ? "var(--brand-primary)" : "var(--ink-4)" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13, color: "var(--ink-2)" }}>{describeActivity(e, t)}</span>
            <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
              {e.actorName ?? "—"} · {fmtDateTime(e.occurredAt)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function describeActivity(e: OfferActivityEntry, t: TFunction): string {
  if (e.type === "STAGE_CHANGE") {
    return e.fromStageName
      ? t("offers.pipeline.activity.moved", "{{from}} → {{to}}", { from: e.fromStageName, to: e.toStageName })
      : t("offers.pipeline.activity.created", "Created in {{stage}}", { stage: e.toStageName });
  }
  switch (e.action) {
    case "CREATE": return t("offers.pipeline.activity.audit.create", "Offer created");
    case "DELETE": return t("offers.pipeline.activity.audit.delete", "Offer deleted");
    default: return t("offers.pipeline.activity.audit.update", "Offer updated");
  }
}

function initials(name: string | null | undefined): string {
  const display = name?.trim();
  if (!display) return "?";
  return display.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

/** "15 May 2026, 14:32" in the user's locale, EU 24h clock — mirrors AdminCompanyDetailPage's formatDateTime idiom. */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(intlLocale(), {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function tagChip(color: string | null): CSSProperties {
  const bg = color ? `color-mix(in srgb, ${color} 18%, var(--surface))` : "var(--surface-muted)";
  const fg = color ? "var(--ink)" : "var(--ink-2)";
  return {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: bg, color: fg, padding: "3px 9px", borderRadius: 999,
    border: `1px solid ${color ?? "var(--border)"}`,
    fontSize: 11.5, fontWeight: 600,
  };
}

function stepBtn(state: PipelineStep["state"], color: string): CSSProperties {
  const base: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600,
    border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer",
    whiteSpace: "nowrap",
  };
  if (state === "current") return { ...base, borderColor: color, color: "var(--ink)", background: tintOf(color), cursor: "default" };
  if (state === "done") return { ...base, color: "var(--ink)", background: tintOf(color), borderColor: color };
  return { ...base, color: "var(--ink-4)" };
}

function stepDot(state: PipelineStep["state"], color: string): CSSProperties {
  return {
    width: 8, height: 8, borderRadius: "50%", flex: "none",
    background: state === "upcoming" ? "transparent" : color,
    border: `1.5px solid ${state === "upcoming" ? "var(--border-strong)" : color}`,
  };
}

function outcomeBtn(active: boolean, color: string): CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600,
    border: `1px solid ${active ? color : "var(--border)"}`,
    background: active ? tintOf(color) : "var(--surface)",
    color: active ? "var(--ink)" : "var(--ink-3)",
    cursor: active ? "default" : "pointer", whiteSpace: "nowrap",
  };
}

/** Same colour mix as offersBoard.ts's `tint` — duplicated locally to avoid exporting a function this file needs for arbitrary (non-stage) colors like the status pair. */
function tintOf(color: string): string {
  if (!/^(var\(--[a-z0-9-]+\)|#[0-9a-f]{6})$/i.test(color)) return "var(--surface-2)";
  return `color-mix(in srgb, ${color} 12%, var(--surface))`;
}

function tabBtnStyle(active: boolean): CSSProperties {
  return {
    padding: "6px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600,
    border: "none", cursor: "pointer", transition: "background .12s",
    color: active ? "var(--on-primary)" : "var(--ink-2)",
    background: active ? "var(--brand-primary)" : "transparent",
  };
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)", padding: 18,
};
const stepperRow: CSSProperties = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, rowGap: 8 };
const stepWrap: CSSProperties = { display: "flex", alignItems: "center" };
const connector: CSSProperties = { width: 20, height: 2, marginRight: 6 };
const outcomeDivider: CSSProperties = { width: 1, height: 22, background: "var(--border)", margin: "0 10px" };
const fieldsRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 4, borderTop: "1px solid var(--border)" };
const fieldGroup: CSSProperties = { display: "flex", flexDirection: "column", gap: 6, paddingTop: 12 };
const fieldLabel: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const overdueBadge: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "var(--err)", background: "var(--err-bg)", padding: "2px 8px", borderRadius: 999,
};
const tagX: CSSProperties = { background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 };
const tabBar: CSSProperties = {
  display: "inline-flex", padding: 4, gap: 4, background: "var(--surface-2)",
  border: "1px solid var(--border)", borderRadius: 999, width: "fit-content", marginTop: 14,
};
const noteCard: CSSProperties = { padding: 10, border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface-2)" };
const noteHead: CSSProperties = { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-3)" };
const authorAvatar: CSSProperties = {
  display: "grid", placeItems: "center", width: 22, height: 22, borderRadius: 999,
  background: "var(--brand-primary)", color: "var(--on-primary)", fontSize: 9.5, fontWeight: 700,
};
const activityRow: CSSProperties = { display: "flex", gap: 8, alignItems: "flex-start" };
const activityDot: CSSProperties = { width: 8, height: 8, borderRadius: "50%", flex: "none", marginTop: 5 };
